import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, Sun, Ticket, Hourglass, Car, Landmark, Lightbulb, Check, Navigation, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsAppDock from "@/components/WhatsAppDock";
import { photoSrc } from "@/components/Reviews";
import { api, waLink } from "@/lib/api";
import { cabFare, directionsUrl } from "@/components/Places";
import WishlistDock, { HeartButton } from "@/components/WishlistDock";
import { shareLink } from "@/components/Logo";

const ZONE_LABEL = { puri: "Puri town", around: "Around Puri", bhubaneswar: "Bhubaneswar & beyond", odisha: "Rest of Odisha" };

function useSeo(place) {
  useEffect(() => {
    if (!place) return;
    const prevTitle = document.title;
    document.title = `${place.name} — timings, entry fee, history & how to reach from Puri | Random Rider`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    const prevDesc = meta.content;
    meta.content = `${place.name}: ${place.tagline}. ${place.distance}. Timings ${place.timings}. Entry ${place.entry_fee}. Best time ${place.best_time}. Book a cab or bike from Puri with Random Rider.`.slice(0, 300);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "place-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "TouristAttraction", name: place.name, description: place.description, image: place.image,
      geo: { "@type": "GeoCoordinates", latitude: place.lat, longitude: place.lng },
      address: { "@type": "PostalAddress", addressRegion: "Odisha", addressCountry: "IN" },
      openingHours: place.timings, isAccessibleForFree: /^free/i.test(place.entry_fee || ""), touristType: ZONE_LABEL[place.zone],
    });
    document.head.appendChild(ld);
    return () => { document.title = prevTitle; meta.content = prevDesc; ld.remove(); };
  }, [place]);
}

function Fact({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex gap-3 bg-white rounded-2xl border border-ink/10 p-4" data-testid={testId}>
      <span className="w-9 h-9 rounded-full bg-terra/10 text-terra flex items-center justify-center shrink-0"><Icon size={16} /></span>
      <div className="min-w-0"><p className="text-[10px] font-mono uppercase tracking-widest text-ink/50">{label}</p><p className="text-sm text-ink font-medium leading-snug">{value || "—"}</p></div>
    </div>
  );
}

export default function PlaceDetail() {
  const { slug } = useParams();
  const [place, setPlace] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setPlace(null); setError(false);
    api.get(`/places/${slug}`).then((r) => setPlace(r.data)).catch(() => setError(true));
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [slug]);
  useSeo(place);

  const share = () => shareLink("places", place.slug, place.name, () => toast.success("Share link copied — paste it in WhatsApp"));

  if (error) return (
    <main className="min-h-screen bg-sand flex flex-col items-center justify-center gap-4" data-testid="place-not-found">
      <p className="font-serif text-3xl text-ink">Place not found</p>
      <Link to="/#places" className="text-terra font-semibold">← All places to visit</Link>
    </main>
  );
  if (!place) return <div className="min-h-screen bg-sand flex items-center justify-center text-ink/50 text-sm">Loading…</div>;

  const fare = cabFare(place.km_from_puri);
  return (
    <main className="bg-sand min-h-screen" data-testid="place-detail-page">
      <Nav />
      <section className="relative h-[60vh] min-h-[420px] mt-[72px]">
        <img src={photoSrc(place.image)} alt={place.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-12 text-sand">
          <Link to="/#places" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-sand/70 hover:text-sand mb-6 w-max" data-testid="place-back-link"><ArrowLeft size={13} /> All places</Link>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-3">{ZONE_LABEL[place.zone]} · {place.tagline}</p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl" data-testid="place-page-title">{place.name}</motion.h1>
          <div className="mt-5 flex flex-wrap gap-3 text-sm">
            <span className="flex items-center gap-1.5 bg-sand/15 backdrop-blur px-3.5 py-1.5 rounded-full"><MapPin size={14} /> {place.distance}</span>
            <span className="flex items-center gap-1.5 bg-sand/15 backdrop-blur px-3.5 py-1.5 rounded-full"><Hourglass size={14} /> {place.ideal_duration}</span>
            <button onClick={share} className="flex items-center gap-1.5 bg-terra px-3.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors" data-testid="place-share-button"><Share2 size={14} /> Share</button>
            <HeartButton item={{ id: `place-${place.id}`, type: "place", name: place.name, slug: place.slug, image: place.image, meta: place.distance }} />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          <p className="text-lg text-ink/80 leading-relaxed" data-testid="place-page-description">{place.description}</p>
          {place.history && (
            <div className="bg-white rounded-3xl border border-ink/10 p-7" data-testid="place-page-history">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra flex items-center gap-2 mb-3"><Landmark size={13} /> History & legend</p>
              <p className="text-base text-ink/80 leading-relaxed">{place.history}</p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <Fact icon={Clock} label="Timings" value={place.timings} testId="place-page-timings" />
            <Fact icon={Sun} label="Best time to visit" value={place.best_time} testId="place-page-best-time" />
            <Fact icon={Ticket} label="Entry fee" value={place.entry_fee} testId="place-page-entry-fee" />
            <Fact icon={Car} label="Approx. cab from Puri (one way)" testId="place-page-cab-fare"
              value={place.km_from_puri > 1 ? `₹${fare.toLocaleString("en-IN")} · ${place.km_from_puri} km × ₹${place.km_from_puri <= 50 ? 75 : 25}/km` : "Walkable / auto-rickshaw ₹50–100"} />
          </div>
          {place.highlights?.length > 0 && (
            <div data-testid="place-page-highlights">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">Don't miss</p>
              <ul className="grid sm:grid-cols-2 gap-2">{place.highlights.map((h) => <li key={h} className="flex items-start gap-2 text-sm text-ink/80 bg-white border border-ink/10 rounded-xl px-4 py-3"><Check size={15} className="text-terra shrink-0 mt-0.5" /> {h}</li>)}</ul>
            </div>
          )}
          {place.tips && (
            <div className="flex gap-3 bg-gold/15 border border-gold/40 rounded-2xl p-5" data-testid="place-page-tips">
              <Lightbulb size={20} className="text-terra shrink-0 mt-0.5" />
              <p className="text-sm text-ink/80 leading-relaxed"><span className="font-semibold">Local tip: </span>{place.tips}</p>
            </div>
          )}
          {place.lat && place.lng ? (
            <div data-testid="place-page-map">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">On the map</p>
              <div className="rounded-3xl overflow-hidden border border-ink/10 h-72 sm:h-96 bg-white">
                <iframe title={`${place.name} map`} src={`https://maps.google.com/maps?q=${place.lat},${place.lng}&z=13&output=embed`} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
              </div>
            </div>
          ) : null}
          {place.related?.length > 0 && (
            <div data-testid="place-page-related">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">Nearby & similar</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {place.related.map((r) => (
                  <Link key={r.slug} to={`/places/${r.slug}`} data-testid={`place-related-${r.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-ink/10 hover:shadow-lg transition-shadow">
                    <img src={photoSrc(r.image)} alt={r.name} loading="lazy" className="h-24 w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="p-3"><p className="text-sm font-semibold text-ink leading-snug">{r.name}</p><p className="text-[10px] text-ink/50 mt-0.5">{r.tagline}</p></div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start bg-ink text-sand rounded-3xl p-7 relative overflow-hidden" data-testid="place-page-cta">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-terra/20 blur-3xl" />
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Get there with us</p>
          <p className="font-serif text-3xl font-black mt-1">{place.km_from_puri > 1 ? `₹${fare.toLocaleString("en-IN")}` : "Free"}</p>
          <p className="text-sm text-sand/60 mt-1">{place.km_from_puri > 1 ? `Approx. cab from our Puri office, one way · ${place.km_from_puri} km` : "Walking distance from our Puri office"}</p>
          <ul className="mt-5 space-y-1.5 text-sm text-sand/80">
            <li className="flex items-center gap-2"><Check size={13} className="text-terra" /> AC car with driver, tolls & parking included</li>
            <li className="flex items-center gap-2"><Check size={13} className="text-terra" /> Or ride yourself on a Meteor / Scooty</li>
            <li className="flex items-center gap-2"><Check size={13} className="text-terra" /> Local guide on request</li>
          </ul>
          <a href={waLink(`Hi Random Rider! I want to visit ${place.name}${place.km_from_puri > 1 ? ` (${place.km_from_puri} km from Puri)` : ""}. Please share cab / bike options and best timing.`)} target="_blank" rel="noreferrer" data-testid="place-page-whatsapp-button"
            className="mt-6 flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-semibold py-3.5 rounded-full hover:brightness-110 transition-all"><MessageCircle size={16} /> Plan this visit on WhatsApp</a>
          {place.lat && place.lng ? (
            <a href={directionsUrl(place)} target="_blank" rel="noreferrer" data-testid="place-page-directions-button" className="mt-2 flex items-center justify-center gap-2 w-full border border-sand/20 text-sand font-semibold py-3 rounded-full hover:bg-sand hover:text-ink transition-colors"><Navigation size={15} /> Directions from Puri</a>
          ) : null}
          <Link to="/#package" data-testid="place-page-package-link" className="mt-2 flex items-center justify-center gap-2 w-full text-sand/70 text-sm py-2 hover:text-gold transition-colors">Add to a custom package →</Link>
        </aside>
      </section>
      <Footer />
      <WhatsAppDock />
      <WishlistDock />
    </main>
  );
}
